import { Fragment, useId, useRef, type KeyboardEvent } from "react";
import { Mic, Image as ImageIcon, Video, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import LeadTagChips from "@/components/leadTags/LeadTagChips";
import InboxFilters, { type InboxFilterState } from "./InboxFilters";

export type InboxMessageKind = "text" | "audio" | "image" | "video" | "document";

export type InboxRow = {
  id: string;
  name: string;
  lastMessage:
    | { kind: InboxMessageKind; content: string; fromHuman: boolean }
    | null;
  unread: boolean;
  // The customer wrote last, so someone is waiting on us. Same predicate the
  // tablero cards use (leadNeedsReply, components/tablero/types.ts):
  // lastMessageSource === "customer".
  needsReply: boolean;
  lastMessageAt: string;
  assignment: { kind: "assigned"; assigneeName: string; isSelf: boolean } | { kind: "ai" };
  tagIds: string[];
};

export type InboxView = "all" | "mine" | "team" | "ai";

type InboxListPanelProps = {
  view: InboxView;
  onViewChange: (next: InboxView) => void;
  showTeamTab: boolean;
  rows: InboxRow[] | undefined;
  /**
   * Rows matched by name in Tinybird rather than by handle in DynamoDB. They sit
   * at the end of `rows`, under a divider naming them — so an ordinary phone or
   * username search, which adds none, reads as one clean list.
   */
  nonRealtimeIds?: Set<string>;
  unreadMineCount: number;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedLeadId?: string;
  onOpenConversation: (id: string) => void;
  onNewConversation?: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  filters: InboxFilterState;
  onFiltersChange: (next: Partial<InboxFilterState>) => void;
  pipelines: { id: string; name: string }[];
  employees: { id: string; name: string }[];
  tags: { id: string; name: string }[];
  isSearching?: boolean;
  hideTabs?: boolean;
  ownEmployees?: { id: string; name: string }[];
  ownEmployeeId?: string;
  onOwnEmployeeChange?: (id: string) => void;
};

const parseUtc = (value: string): Date => {
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const normalized = hasTz ? value : `${value.replace(" ", "T")}Z`;
  return new Date(normalized);
};

const formatRelative = (iso: string): string => {
  const date = parseUtc(iso);
  const then = date.getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "ahora";
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHr = Math.floor(diffMs / 3_600_000);
  if (diffHr < 24) return `hace ${diffHr} h`;
  const diffDay = Math.floor(diffMs / 86_400_000);
  if (diffDay === 1) return "ayer";
  if (diffDay < 7) return `hace ${diffDay} d`;
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    timeZone: "America/Bogota",
  });
};

const formatBadge = (n: number): string => (n > 99 ? "99+" : String(n));

const TabButton = ({
  id,
  panelId,
  selected,
  onActivate,
  label,
  count,
  tabIndex,
  onKeyDown,
  buttonRef,
}: {
  id: string;
  panelId: string;
  selected: boolean;
  onActivate: () => void;
  label: string;
  count?: number;
  tabIndex: number;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) => (
  <button
    ref={buttonRef}
    id={id}
    role="tab"
    type="button"
    aria-selected={selected}
    aria-controls={panelId}
    tabIndex={tabIndex}
    onClick={onActivate}
    onKeyDown={onKeyDown}
    className={cn(
      "relative inline-flex items-center gap-2 border-0 bg-transparent px-1 pt-3 pb-3.5 text-sm leading-none transition-colors duration-150",
      "focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-palomma-primary",
      selected
        ? "font-semibold text-palomma-secondary"
        : "font-medium text-muted-foreground hover:text-palomma-secondary"
    )}
  >
    <span>{label}</span>
    {count != null && count > 0 && (
      <span
        aria-label={`${formatBadge(count)} sin leer`}
        className={cn(
          "min-w-[20px] rounded-full px-2 py-[3px] text-center text-[11px] font-medium leading-none tracking-wide",
          selected ? "bg-palomma-primary text-white" : "bg-muted text-palomma-secondary"
        )}
      >
        {formatBadge(count)}
      </span>
    )}
    {selected && (
      <span
        aria-hidden="true"
        className="absolute inset-x-0 -bottom-px h-0.5 rounded-t-sm bg-palomma-primary"
      />
    )}
  </button>
);

const MetaTag = ({
  variant,
  label,
  truncate = false,
}: {
  variant: "assigned" | "ai";
  label: string;
  truncate?: boolean;
}) => (
  <span
    title={truncate ? label : undefined}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-palomma-secondary",
      truncate && "max-w-[10rem]"
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        variant === "ai" ? "bg-palomma-primary" : "bg-muted-foreground"
      )}
    />
    <span className={cn(truncate && "min-w-0 truncate")}>{label}</span>
  </span>
);

const MEDIA_LABEL: Record<Exclude<InboxMessageKind, "text">, string> = {
  audio: "Audio",
  image: "Foto",
  video: "Video",
  document: "Documento",
};

const MediaIcon = ({ kind }: { kind: Exclude<InboxMessageKind, "text"> }) => {
  const className = "size-3.5 shrink-0";
  if (kind === "audio") return <Mic aria-hidden="true" className={className} />;
  if (kind === "image") return <ImageIcon aria-hidden="true" className={className} />;
  if (kind === "video") return <Video aria-hidden="true" className={className} />;
  return <FileText aria-hidden="true" className={className} />;
};

const collapseWhitespace = (s: string): string => s.replace(/\s+/g, " ").trim();

const Snippet = ({
  message,
  unread,
}: {
  message: InboxRow["lastMessage"];
  unread: boolean;
}) => {
  if (!message) {
    return <span className="truncate italic">Sin mensajes aún</span>;
  }

  const prefix = message.fromHuman ? (
    <span
      className={cn(
        "shrink-0",
        unread ? "text-palomma-secondary" : "text-muted-foreground"
      )}
    >
      Tú:
    </span>
  ) : null;

  if (message.kind === "text") {
    const text = collapseWhitespace(message.content);
    return (
      <>
        {prefix}
        <span className="min-w-0 truncate">{text || "Mensaje vacío"}</span>
      </>
    );
  }

  return (
    <>
      {prefix}
      <span className="inline-flex min-w-0 items-center gap-1 truncate">
        <MediaIcon kind={message.kind} />
        <span className="truncate">{MEDIA_LABEL[message.kind]}</span>
      </span>
    </>
  );
};

const Row = ({
  row,
  selected,
  onOpen,
}: {
  row: InboxRow;
  selected: boolean;
  onOpen: () => void;
}) => {
  const handleKey = (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <li
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onOpen}
      onKeyDown={handleKey}
      aria-label={[row.name, row.unread ? "Sin leer" : null, row.needsReply ? "Sin responder" : null]
        .filter(Boolean)
        .join(". ")}
      className={cn(
        "flex cursor-pointer flex-col items-stretch gap-1 border-t border-border px-4 py-3.5 transition-colors duration-100 first:border-t-0 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-1.5 md:px-5",
        selected
          ? "bg-gray-tertiary"
          : "hover:bg-gray-tertiary focus:outline-none focus-visible:bg-gray-tertiary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-palomma-primary"
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 text-[15px] font-semibold leading-tight text-palomma-secondary">
          {/* Needs-reply takes precedence over unread in this slot, and the two
              coexist rather than compete: unread still says itself through the
              snippet's weight and the bold purple timestamp beside it, so red
              winning the dot costs the row nothing. Encoding both here — red dot,
              unread ring — was one signal too many for a 10px target.

              The tablero marks the same lead with a red dot AND a red card outline;
              a per-row outline would stack fifty red boxes down a dense list, so the
              dot carries it alone here. Deliberately not the tablero's animate-ping
              either: one pinging dot on a board is a signal, twenty down a scrolling
              list is a disco. */}
          <span
            aria-hidden="true"
            title={row.needsReply ? "Sin responder — el cliente escribió de último" : undefined}
            className={cn(
              "size-2.5 shrink-0 rounded-full",
              row.needsReply
                ? "bg-danger"
                : row.unread
                  ? "bg-palomma-primary ring-2 ring-palomma-primary/20"
                  : "bg-transparent"
            )}
          />
          <span className="truncate">{row.name}</span>
        </div>
        <div
          className={cn(
            "mt-0.5 flex min-w-0 items-center gap-1.5 pl-[18px] text-[13px] leading-snug",
            row.unread ? "font-medium text-palomma-secondary" : "font-normal text-muted-foreground"
          )}
        >
          <Snippet message={row.lastMessage} unread={row.unread} />
        </div>
      </div>
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 whitespace-nowrap pl-[18px] text-[11px] md:flex-col md:items-end md:gap-1.5 md:pl-0",
          row.unread
            ? "font-semibold text-palomma-primary"
            : "text-muted-foreground"
        )}
      >
        <span className="tabular-nums">{formatRelative(row.lastMessageAt)}</span>
        {row.assignment.kind === "ai" ? (
          <MetaTag variant="ai" label="IA" />
        ) : (
          <MetaTag
            variant="assigned"
            label={row.assignment.isSelf ? "Tú" : row.assignment.assigneeName}
            truncate={!row.assignment.isSelf}
          />
        )}
      </div>
      <LeadTagChips tagIds={row.tagIds} max={2} className="pl-[18px] md:basis-full" />
    </li>
  );
};

const SkeletonList = () => (
  <div
    aria-busy="true"
    aria-label="Cargando conversaciones"
    className="rounded-b-xl border border-t-0 border-border bg-white"
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="grid grid-cols-[minmax(0,1fr)_80px] items-center gap-4 border-t border-border px-5 py-3.5 first:border-t-0"
      >
        <div>
          <div className="h-2.5 w-[55%] animate-[shimmer_1.4s_linear_infinite] rounded-sm bg-[linear-gradient(90deg,#f5f5f5_0%,#dfdee2_50%,#f5f5f5_100%)] bg-size-[200%_100%] motion-reduce:animate-none" />
          <div className="mt-2 h-2.5 w-[40%] animate-[shimmer_1.4s_linear_infinite] rounded-sm bg-[linear-gradient(90deg,#f5f5f5_0%,#dfdee2_50%,#f5f5f5_100%)] bg-size-[200%_100%] motion-reduce:animate-none" />
        </div>
        <div className="h-2 w-[60px] animate-[shimmer_1.4s_linear_infinite] rounded-sm bg-[linear-gradient(90deg,#f5f5f5_0%,#dfdee2_50%,#f5f5f5_100%)] bg-size-[200%_100%] motion-reduce:animate-none" />
      </div>
    ))}
  </div>
);

const EmptyMine = ({ onJumpToAi }: { onJumpToAi: () => void }) => (
  <div className="rounded-b-xl border border-t-0 border-border bg-white px-6 pb-14 pt-12 text-center">
    <p className="m-0 text-base font-semibold text-palomma-secondary">
      No tienes conversaciones asignadas.
    </p>
    <p className="mx-auto mb-4 mt-1.5 max-w-[36ch] text-sm text-muted-foreground">
      La IA sigue atendiendo a los leads en segundo plano.
    </p>
    <button
      type="button"
      onClick={onJumpToAi}
      className="rounded-sm text-sm font-medium text-palomma-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary"
    >
      Ver lo que la IA está atendiendo →
    </button>
  </div>
);

const EmptyTeam = () => (
  <div className="rounded-b-xl border border-t-0 border-border bg-white px-6 pb-14 pt-12 text-center">
    <p className="m-0 text-base font-semibold text-palomma-secondary">
      Tu equipo no tiene conversaciones asignadas.
    </p>
    <p className="mx-auto mt-1.5 max-w-[36ch] text-sm text-muted-foreground">
      Cuando se asigne una conversación a alguien del equipo, aparecerá aquí.
    </p>
  </div>
);

const EmptyAll = () => (
  <div className="rounded-b-xl border border-t-0 border-border bg-white px-6 pb-14 pt-12 text-center">
    <p className="m-0 text-base font-semibold text-palomma-secondary">
      Aún no hay conversaciones.
    </p>
    <p className="mx-auto mt-1.5 max-w-[36ch] text-sm text-muted-foreground">
      Cuando llegue un nuevo lead, aparecerá aquí.
    </p>
  </div>
);

const EmptyAi = () => (
  <div className="rounded-b-xl border border-t-0 border-border bg-white px-6 pb-14 pt-12 text-center">
    <p className="m-0 text-base font-semibold text-palomma-secondary">
      No hay conversaciones con la IA ahora.
    </p>
    <p className="mx-auto mt-1.5 max-w-[36ch] text-sm text-muted-foreground">
      Cuando llegue un nuevo lead, aparecerá aquí mientras la IA lo atiende.
    </p>
  </div>
);

const NoResults = ({ hasSearch, onClear }: { hasSearch: boolean; onClear: () => void }) => {
  const headline = hasSearch
    ? "No encontramos conversaciones."
    : "Ningún resultado con esos filtros.";
  const body = hasSearch
    ? "Revisa el término de búsqueda o quita filtros."
    : "Prueba con otra combinación de filtros.";
  return (
    <div className="rounded-b-xl border border-t-0 border-border bg-white px-6 pb-14 pt-12 text-center">
      <p className="m-0 text-base font-semibold text-palomma-secondary">{headline}</p>
      <p className="mx-auto mb-4 mt-1.5 max-w-[36ch] text-sm text-muted-foreground">{body}</p>
      <button
        type="button"
        onClick={onClear}
        className="rounded-sm text-sm font-medium text-palomma-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary"
      >
        Limpiar búsqueda y filtros
      </button>
    </div>
  );
};

const InboxListPanel = ({
  view,
  onViewChange,
  showTeamTab,
  rows,
  nonRealtimeIds,
  unreadMineCount,
  hasMore,
  onLoadMore,
  selectedLeadId,
  onOpenConversation,
  onNewConversation,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  pipelines,
  employees,
  tags,
  isSearching,
  hideTabs,
  ownEmployees,
  ownEmployeeId,
  onOwnEmployeeChange,
}: InboxListPanelProps): JSX.Element => {
  const reactId = useId();
  const order: InboxView[] = showTeamTab
    ? ["all", "mine", "team", "ai"]
    : ["all", "mine", "ai"];
  const tabIds: Record<InboxView, string> = {
    all: `tab-all-${reactId}`,
    mine: `tab-mine-${reactId}`,
    team: `tab-team-${reactId}`,
    ai: `tab-ai-${reactId}`,
  };
  const panelId = `panel-inbox-${reactId}`;
  const tabRefs = useRef<Record<InboxView, HTMLButtonElement | null>>({
    all: null,
    mine: null,
    team: null,
    ai: null,
  });

  const handleTabKey = (current: InboxView) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const idx = order.indexOf(current);
    let next: InboxView | undefined;
    if (e.key === "ArrowRight") next = order[(idx + 1) % order.length];
    else if (e.key === "ArrowLeft") next = order[(idx - 1 + order.length) % order.length];
    else if (e.key === "Home") next = order[0];
    else if (e.key === "End") next = order[order.length - 1];
    if (next) {
      e.preventDefault();
      onViewChange(next);
      requestAnimationFrame(() => tabRefs.current[next!]?.focus());
    }
  };

  const hasSearch = search.trim().length > 0;
  const hasFilters =
    !!filters.pipelineId ||
    !!filters.pipelinePositionId ||
    !!filters.source ||
    !!filters.employeeId ||
    !!filters.tagIds?.length;
  const isFiltering = hasSearch || hasFilters;

  const showEmployeeFilter = view === "team";

  const clearAll = () => {
    onSearchChange("");
    onFiltersChange({
      pipelineId: undefined,
      pipelinePositionId: undefined,
      source: undefined,
      employeeId: undefined,
      tagIds: undefined,
    });
  };

  const renderBody = () => {
    if (rows === undefined) return <SkeletonList />;
    if (rows.length === 0) {
      if (isFiltering) return <NoResults hasSearch={hasSearch} onClear={clearAll} />;
      if (view === "all") return <EmptyAll />;
      if (view === "mine") return <EmptyMine onJumpToAi={() => onViewChange("ai")} />;
      if (view === "team") return <EmptyTeam />;
      return <EmptyAi />;
    }
    // Absent unless the name half actually contributed rows.
    const firstNonRealtimeId = rows.find((r) => nonRealtimeIds?.has(r.id))?.id;
    return (
      <ul className="m-0 list-none rounded-b-xl border border-t-0 border-border bg-white p-0">
        {rows.map((row) => (
          <Fragment key={row.id}>
            {row.id === firstNonRealtimeId && (
              <li className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
                Coincidencias por nombre · datos no en tiempo real
              </li>
            )}
            <Row
              row={row}
              selected={row.id === selectedLeadId}
              onOpen={() => onOpenConversation(row.id)}
            />
          </Fragment>
        ))}
      </ul>
    );
  };

  const tabLabels: Record<InboxView, string> = {
    all: "Todas",
    mine: "Mías",
    team: "Equipo",
    ai: "IA",
  };

  return (
    <main className="mx-auto h-full w-full max-w-[1040px] overflow-y-auto px-3 pb-20 pt-4 md:px-6 md:pt-6">
      <InboxFilters
        search={search}
        onSearchChange={onSearchChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
        pipelines={pipelines}
        employees={employees}
        tags={tags}
        showEmployeeFilter={showEmployeeFilter}
        isSearching={isSearching}
        onNewConversation={onNewConversation}
      />

      {isFiltering && rows && rows.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {hasSearch
            ? `${rows.length}${hasMore ? "+" : ""} ${
                rows.length === 1 ? "resultado" : "resultados"
              } para «${search.trim()}»`
            : `${rows.length}${hasMore ? "+" : ""} ${
                rows.length === 1 ? "resultado" : "resultados"
              } con los filtros aplicados`}
        </p>
      )}

      {!hideTabs && (
        <div className="border-b border-border">
          <div role="tablist" aria-label="Filtro de bandeja" className="flex gap-3">
            {order.map((v) => (
              <TabButton
                key={v}
                id={tabIds[v]}
                panelId={panelId}
                selected={view === v}
                onActivate={() => onViewChange(v)}
                label={tabLabels[v]}
                count={v === "mine" ? unreadMineCount : undefined}
                tabIndex={view === v ? 0 : -1}
                onKeyDown={handleTabKey(v)}
                buttonRef={(el) => {
                  tabRefs.current[v] = el;
                }}
              />
            ))}
          </div>
        </div>
      )}

      {view === "mine" && ownEmployees && ownEmployees.length >= 2 && (
        <div
          role="group"
          aria-label="Asesor"
          className="flex gap-2 overflow-x-auto border-b border-border py-2"
        >
          {ownEmployees.map((employee) => {
            const selected = employee.id === ownEmployeeId;
            return (
              <button
                key={employee.id}
                type="button"
                aria-pressed={selected}
                onClick={() => onOwnEmployeeChange?.(employee.id)}
                className={cn(
                  "inline-flex min-h-11 shrink-0 items-center rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary md:min-h-8",
                  selected
                    ? "border-palomma-primary bg-palomma-primary text-white"
                    : "border-border bg-white text-palomma-secondary hover:bg-muted"
                )}
              >
                <span className="max-w-[160px] truncate">{employee.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <section id={panelId} role="tabpanel" aria-labelledby={tabIds[view]}>
        {renderBody()}
        {hasMore && rows && rows.length > 0 && (
          <div className="border-t border-border bg-white p-4">
            <Button variant="outline" size="sm" onClick={onLoadMore} className="w-full">
              Cargar más
            </Button>
          </div>
        )}
      </section>
    </main>
  );
};

export default InboxListPanel;
